// components/TrialBanner.tsx
// ─────────────────────────────────────────────────────────────
// Upsell / renewal banner shown when subscription is near expiry.
// • Color: indigo/cyan gradient — never red.
// • Smart recommendation based on agent + shop count.
// • Desktop: 3 quick-pay buttons (daily / weekly / monthly).
// • Tablet:  recommended button + "All plans" fallback.
// • Mobile:  single CTA that opens PaywallScreen.
// • Two-step confirm on quick-pay so owners don't misfire.
// • Auto-dismisses on successful payment.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import PaywallScreen from "./PaywallScreen";

type Freq = "daily" | "weekly" | "monthly";

function getRecommendedFreq(agents: number, shops: number): Freq {
  const total = agents + shops;
  if (agents <=4 && shops <=3) return "daily";
  
  
  if (total <= 2) return "daily";
  if (total <= 7) return "weekly";
  return "monthly";
}

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

export default function TrialBanner() {
  const { theme }   = useTheme();
  const { profile } = useAuth();
  const {
    subscription, status, daysLeft, trialTimeLeft, isNearExpiry, plans,
    triggerStkPush, paymentPending, paymentError, paymentSuccess,
  } = useSubscription();

  const width      = useWindowWidth();
  const isMobile   = width < 640;
  const isDesktop  = width >= 1024;

  const [dismissed,   setDismissed]   = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [pendingFreq, setPendingFreq] = useState<Freq | null>(null);
  const [confirmFreq, setConfirmFreq] = useState<Freq | null>(null);
  const [agentCount,  setAgentCount]  = useState(0);
  const [shopCount,   setShopCount]   = useState(0);

  // Fetch agent + shop counts for smart recommendation
  useEffect(() => {
    if (!profile?.id || !isNearExpiry) return;
    Promise.all([
      supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("owner_id", profile.id)
        .eq("role", "agent"),
      supabase
        .from("shops")
        .select("id", { count: "exact", head: true })
        .eq("owner_id", profile.id),
    ]).then(([a, s]) => {
      setAgentCount(a.count ?? 0);
      setShopCount(s.count ?? 0);
    });
  }, [profile?.id, isNearExpiry]);

  // Auto-dismiss when payment succeeds
  useEffect(() => {
    if (paymentSuccess) {
      setPendingFreq(null);
      setConfirmFreq(null);
      setDismissed(true);
    }
  }, [paymentSuccess]);

  // Clear pending state on error
  useEffect(() => {
    if (paymentError) {
      setPendingFreq(null);
      setConfirmFreq(null);
    }
  }, [paymentError]);

  if (!isNearExpiry || dismissed) return null;

  // ── Plan data ────────────────────────────────────────────────
  const isCritical   = daysLeft <= 1;
  const isTrialing   = status === "trialing";

  // Find the plan with daily/weekly pricing (starter-tier)
  const flexPlan     = plans.find(p => p.price_daily != null) ?? plans[0] ?? null;
  const planId       = flexPlan?.id ?? "";
  const priceDaily   = flexPlan?.price_daily  ?? 25;
  const priceWeekly  = flexPlan?.price_weekly ?? 200;
  const priceMonthly = flexPlan?.price        ?? 499;

  const recommended  = getRecommendedFreq(agentCount, shopCount);
  const savedPhone   = subscription?.last_payment_phone ?? null;

  // ── Labels ───────────────────────────────────────────────────
  const timeLabel = isTrialing
    ? trialTimeLeft ? `trial ends in ${trialTimeLeft}` : "trial ends today"
    : daysLeft === 0 ? "access expires today"
    : `access expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`;

  const hasActivity = agentCount > 0 || shopCount > 0;
  const recFreqName = recommended === "daily" ? "daily" : recommended === "weekly" ? "weekly" : "monthly";
  const recPrice    = recommended === "daily" ? priceDaily : recommended === "weekly" ? priceWeekly : priceMonthly;
  const recPriceStr = recommended === "daily"
    ? `KSh ${priceDaily}/day`
    : recommended === "weekly"
    ? `KSh ${priceWeekly}/week`
    : `KSh ${priceMonthly}/month`;

  const subTitle = hasActivity
    ? `For your ${agentCount} agent${agentCount !== 1 ? "s" : ""}${shopCount > 0 ? ` & ${shopCount} shop${shopCount !== 1 ? "s" : ""}` : ""}, we suggest ${recFreqName} (${recPriceStr}).`
    : `We have packages from KSh ${priceDaily}/day · KSh ${priceWeekly}/week · KSh ${priceMonthly}/month.`;

  // ── Colors ───────────────────────────────────────────────────
  // Amber for critical last-day; indigo/cyan otherwise — never red
  const bg         = isCritical
    ? "linear-gradient(135deg, rgba(251,146,60,0.09), rgba(234,179,8,0.06))"
    : "linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(6,182,212,0.05) 100%)";
  const topBar     = isCritical
    ? "linear-gradient(90deg, #f97316, #fbbf24 60%, transparent)"
    : "linear-gradient(90deg, #6366f1, #06b6d4 60%, transparent)";
  const borderClr  = isCritical ? "rgba(251,146,60,0.22)" : "rgba(99,102,241,0.2)";
  const accentClr  = isCritical ? "#fb923c" : "#818cf8";

  // ── Frequency button options ─────────────────────────────────
  const FREQS: { freq: Freq; label: string; price: number }[] = [
    { freq: "daily",   label: `KSh ${priceDaily}/day`,  price: priceDaily   },
    { freq: "weekly",  label: `KSh ${priceWeekly}/wk`,  price: priceWeekly  },
    { freq: "monthly", label: `KSh ${priceMonthly}/mo`, price: priceMonthly },
  ];

  // ── Quick-pay handler ─────────────────────────────────────────
  // Step 1: first click → show confirm (button label changes to "Send KSh X →")
  // Step 2: click again → fire STK push
  // If no saved phone → open PaywallScreen instead
  const handleQuickPay = async (freq: Freq) => {
    if (paymentPending) return;
    if (!savedPhone) {
      setShowPaywall(true);
      return;
    }
    if (confirmFreq !== freq) {
      setConfirmFreq(freq);   // show confirm label
      return;
    }
    // Confirmed — fire
    setConfirmFreq(null);
    setPendingFreq(freq);
    await triggerStkPush(savedPhone, planId, freq);
  };

  // ── Quick-pay button renderer ─────────────────────────────────
  const QuickPayBtn = ({ freq, label, price }: { freq: Freq; label: string; price: number }) => {
    const isRec       = freq === recommended;
    const isConfirming = confirmFreq === freq;
    const isPending   = pendingFreq === freq && paymentPending;

    return (
      <button
        key={freq}
        onClick={() => handleQuickPay(freq)}
        disabled={paymentPending}
        title={`Pay ${label} via M-Pesa`}
        style={{
          background: isRec
            ? isCritical
              ? "linear-gradient(135deg, #f97316, #eab308)"
              : "linear-gradient(135deg, #6366f1, #4f46e5)"
            : "rgba(255,255,255,0.06)",
          border: isRec
            ? `1px solid ${isCritical ? "rgba(251,146,60,0.5)" : "rgba(99,102,241,0.45)"}`
            : "1px solid rgba(255,255,255,0.1)",
          borderRadius: 8,
          padding: isRec ? "5px 12px" : "5px 9px",
          color: isRec ? "#fff" : theme.text.secondary,
          fontFamily: theme.font.mono,
          fontWeight: isRec ? 700 : 500,
          fontSize: 11,
          cursor: paymentPending ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", gap: 5,
          transition: "all 0.15s",
          whiteSpace: "nowrap",
          opacity: paymentPending && pendingFreq !== freq ? 0.5 : 1,
        }}
        onMouseEnter={e => {
          if (!paymentPending && !isRec)
            (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.1)";
        }}
        onMouseLeave={e => {
          if (!isRec)
            (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.06)";
        }}
      >
        {isPending && (
          <span style={{
            width: 10, height: 10,
            border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff",
            borderRadius: "50%", display: "inline-block",
            animation: "spin 0.7s linear infinite", flexShrink: 0,
          }} />
        )}
        {isConfirming ? `Send KSh ${price.toLocaleString()} →` : label}
        {isRec && !isPending && !isConfirming && (
          <span style={{
            background: "rgba(255,255,255,0.22)", borderRadius: 4,
            padding: "1px 5px", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.03em",
          }}>
            BEST
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <div style={{
        background: bg,
        borderBottom: `1px solid ${borderClr}`,
        padding: isMobile ? "8px 12px" : "10px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: isMobile ? 8 : 14,
        position: "relative",
        overflow: "hidden",
        flexWrap: isMobile ? "wrap" : "nowrap",
      }}>

        {/* Gradient accent line at top edge */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: topBar,
          pointerEvents: "none",
        }} />

        {/* ── Left: icon + text ──────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 15, flexShrink: 0 }}>
            {isCritical ? "⚡" : "✨"}
          </span>
          <div style={{ minWidth: 0 }}>
            <span style={{
              fontSize: 12, fontFamily: theme.font.mono,
              color: accentClr, fontWeight: 700,
            }}>
              Your {timeLabel}.
            </span>
            {!isMobile && (
              <span style={{
                fontSize: 12, fontFamily: theme.font.mono,
                color: theme.text.muted, marginLeft: 6,
              }}>
                {subTitle}{" "}
                <span style={{ color: theme.text.secondary }}>Pay via M-Pesa in seconds.</span>
              </span>
            )}
            {/* Inline error from banner-triggered payment */}
            {paymentError && pendingFreq !== null && (
              <div style={{
                fontSize: 10, color: "#f87171", fontFamily: theme.font.mono,
                marginTop: 2,
              }}>
                {paymentError}
              </div>
            )}
          </div>
        </div>

        {/* ── Right: action buttons ──────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>

          {isDesktop ? (
            // Desktop: all three frequency buttons
            <>
              {FREQS.map(f => <QuickPayBtn key={f.freq} {...f} />)}
              {confirmFreq && (
                <button
                  onClick={() => setConfirmFreq(null)}
                  style={{
                    background: "none",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 6, padding: "4px 9px",
                    color: theme.text.muted, fontSize: 11,
                    fontFamily: theme.font.mono, cursor: "pointer",
                  }}>
                  Cancel
                </button>
              )}
            </>
          ) : isMobile ? (
            // Mobile: single CTA → always opens paywall
            <button
              onClick={() => setShowPaywall(true)}
              style={{
                background: isCritical
                  ? "linear-gradient(135deg, #f97316, #eab308)"
                  : "linear-gradient(135deg, #6366f1, #06b6d4)",
                border: "none", borderRadius: 8,
                padding: "6px 13px",
                color: "#fff", fontFamily: theme.font.mono,
                fontWeight: 700, fontSize: 11,
                cursor: "pointer", whiteSpace: "nowrap",
              }}>
              {isCritical ? "⚡ Renew now" : `From KSh ${priceDaily} →`}
            </button>
          ) : (
            // Tablet: recommended + "All plans"
            <>
              <QuickPayBtn
                freq={recommended}
                label={recPriceStr}
                price={recPrice}
              />
              <button
                onClick={() => setShowPaywall(true)}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8, padding: "5px 10px",
                  color: theme.text.muted, fontFamily: theme.font.mono,
                  fontSize: 11, cursor: "pointer",
                }}>
                All plans
              </button>
            </>
          )}

          {/* Dismiss — always (but not when today is the last day) */}
          {!isCritical && (
            <button
              onClick={() => setDismissed(true)}
              title="Dismiss"
              style={{
                background: "none",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 6, padding: "4px 7px",
                color: theme.text.muted, cursor: "pointer",
                display: "flex", alignItems: "center",
              }}>
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* ── PaywallScreen overlay ─────────────────────────────── */}
      {showPaywall && (
        <div
          onClick={() => setShowPaywall(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
            zIndex: 200, display: "flex", alignItems: "center",
            justifyContent: "center", padding: 20,
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 440, position: "relative" }}>
            <button
              onClick={() => setShowPaywall(false)}
              style={{
                position: "absolute", top: -12, right: -12, zIndex: 10,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "50%", width: 28, height: 28,
                color: "#9ca3af", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
              <X size={13} />
            </button>
            <PaywallScreen />
          </div>
        </div>
      )}
    </>
  );
}
